/**
 * Theme Configuration Registry
 * Central source of truth for all visual themes.
 * Maps each theme to its component set, default style guide, and display metadata.
 */

export const THEMES = {
  classic: {
    id: 'classic',
    name: 'Evergreen',
    description: 'Clean, modern, and versatile. Works for any business.',
    preview_image: '/theme-previews/classic.png',
    recommended_for: ['restaurant', 'dental', 'plumbing', 'roofing', 'medical', 'general', 'cleaning', 'automotive', 'pet', 'travel'],
    best_for_label: 'Professional & service businesses',
    prefix: '',
    defaultStyleGuide: {
      colors: {
        primary: "#059669",
        secondary: "#d97706",
        background: "#fafdf7",
        text: "#0c1f17",
        muted: "#5f7367"
      },
      typography: {
        heading: { font_family: "Outfit", font_weight: 700 },
        body: { font_family: "DM Sans", font_weight: 400 }
      },
      ui: {
        button: {
          border_radius: "10px",
          padding: "14px 28px",
          background: "primary",
          text_color: "#ffffff",
          font_weight: "600",
          box_shadow: "0 4px 14px 0 rgba(5, 150, 105, 0.3)"
        },
        input: {
          border_radius: "8px",
          border_color: "#d4e5d8",
          padding: "14px 16px",
          background: "#ffffff",
          focus_border: "#059669",
          box_shadow: "0 1px 3px 0 rgba(12, 31, 23, 0.06)"
        }
      }
    },
    components: {
      common: ['HEADER_01', 'FOOTER_01'],
      hero: ['HERO_01', 'HERO_02', 'HERO_03', 'HERO_04', 'HERO_05', 'HERO_06'],
      about: ['ABOUT_01', 'ABOUT_02', 'ABOUT_03', 'ABOUT_04', 'ABOUT_05', 'ABOUT_06'],
      services: ['SERVICES_01', 'SERVICES_02', 'SERVICES_03'],
      features: ['FEATURES_01', 'FEATURES_02', 'FEATURES_03', 'FEATURES_04', 'FEATURES_05', 'FEATURES_06'],
      testimonials: ['TESTIMONIAL_01', 'TESTIMONIAL_02', 'TESTIMONIAL_03'],
      contact: ['CONTACT_01', 'CONTACT_02', 'CONTACT_03', 'CONTACT_04', 'CONTACT_05', 'CONTACT_06'],
      cta: ['CTA_01', 'CTA_02', 'CTA_03', 'CTA_05'],
      faq: ['FAQ_01', 'FAQ_02', 'FAQ_03', 'FAQ_05'],
      pricing: ['PRICING_01', 'PRICING_02', 'PRICING_03'],
      stats: ['STATS_01', 'STATS_02', 'STATS_03'],
      logos: ['LOGOS_01', 'LOGOS_02', 'LOGOS_03'],
      content: ['CONTENT_01', 'CONTENT_02', 'CONTENT_03', 'CONTENT_04', 'CONTENT_05', 'CONTENT_06'],
      newsletter: ['NEWSLETTER_01', 'NEWSLETTER_02', 'NEWSLETTER_03'],
      map: ['MAP_01', 'MAP_02', 'MAP_03'],
      team: ['TEAM_01', 'TEAM_02', 'TEAM_03'],
      gallery: ['GALLERY_01', 'GALLERY_02', 'GALLERY_03', 'GALLERY_04', 'GALLERY_05', 'GALLERY_06'],
      hours: ['HOURS_01', 'HOURS_02', 'HOURS_03'],
      menu: ['MENU_01', 'MENU_02', 'MENU_03']
    }
  },

  kale: {
    id: 'kale',
    name: 'Nova',
    description: 'Bold and vivid. Ideal for creative agencies and startups.',
    preview_image: '/theme-previews/kale.png',
    recommended_for: ['agency', 'saas', 'education', 'coaching', 'events'],
    best_for_label: 'Creative agencies & startups',
    prefix: 'KALE_',
    defaultStyleGuide: {
      colors: {
        primary: "#2B2BFF",
        secondary: "#0D0D0D",
        background: "#FFFFFF",
        text: "#0D0D0D",
        muted: "#6B6B6B"
      },
      typography: {
        heading: { font_family: "Inter", font_weight: 800 },
        body: { font_family: "Inter", font_weight: 400 }
      },
      ui: {
        button: {
          border_radius: "50px",
          padding: "14px 32px",
          background: "primary",
          text_color: "#ffffff",
          font_weight: "600",
          box_shadow: "0 10px 30px rgba(43,43,255,0.3)"
        },
        input: {
          border_radius: "12px",
          border_color: "#e0e0e0",
          padding: "14px 16px",
          background: "#ffffff",
          focus_border: "#2B2BFF",
          box_shadow: "0 2px 8px rgba(43,43,255,0.08)"
        }
      }
    },
    components: {
      common: ['KALE_HEADER_01', 'KALE_FOOTER_01'],
      hero: ['KALE_HERO_01'],
      about: ['KALE_ABOUT_01'],
      services: ['KALE_SERVICES_01'],
      features: ['KALE_FEATURES_01'],
      testimonials: ['KALE_TESTIMONIALS_01'],
      contact: ['KALE_CONTACT_01'],
      cta: ['KALE_CTA_01'],
      faq: ['KALE_FAQ_01'],
      pricing: ['KALE_PRICING_01'],
      stats: ['KALE_STATS_01'],
      content: ['KALE_CONTENT_01'],
      newsletter: ['KALE_NEWSLETTER_01'],
      map: ['KALE_MAP_01'],
      team: ['KALE_TEAM_01'],
      gallery: ['KALE_PROJECTS_01'],
      hours: ['KALE_HOURS_01'],
      menu: ['KALE_MENU_01']
    }
  },

  cliaro: {
    id: 'cliaro',
    name: 'Clarity',
    description: 'Fresh and modern. Perfect for SaaS, tech, and digital products.',
    preview_image: '/theme-previews/cliaro.png',
    recommended_for: ['saas', 'ecommerce', 'education', 'nonprofit', 'accounting'],
    best_for_label: 'SaaS, tech & digital products',
    prefix: 'CLIARO_',
    defaultStyleGuide: {
      colors: {
        primary: "#22C55E",
        secondary: "#0F172A",
        background: "#FFFFFF",
        text: "#0F172A",
        muted: "#64748B"
      },
      typography: {
        heading: { font_family: "Inter", font_weight: 800 },
        body: { font_family: "Inter", font_weight: 400 }
      },
      ui: {
        button: {
          border_radius: "50px",
          padding: "14px 32px",
          background: "primary",
          text_color: "#ffffff",
          font_weight: "600",
          box_shadow: "0 10px 30px rgba(34,197,94,0.3)"
        },
        input: {
          border_radius: "12px",
          border_color: "#e2e8f0",
          padding: "14px 16px",
          background: "#ffffff",
          focus_border: "#22C55E",
          box_shadow: "0 2px 8px rgba(34,197,94,0.08)"
        }
      }
    },
    components: {
      common: ['CLIARO_HEADER_01', 'CLIARO_FOOTER_01'],
      hero: ['CLIARO_HERO_01'],
      about: ['CLIARO_ABOUT_01'],
      services: ['CLIARO_SERVICES_01'],
      features: ['CLIARO_FEATURES_01'],
      testimonials: ['CLIARO_TESTIMONIALS_01'],
      contact: ['CLIARO_CONTACT_01'],
      cta: ['CLIARO_CTA_01'],
      faq: ['CLIARO_FAQ_01'],
      pricing: ['CLIARO_PRICING_01'],
      stats: ['CLIARO_STATS_01'],
      content: ['CLIARO_CONTENT_01'],
      newsletter: ['CLIARO_NEWSLETTER_01'],
      map: ['CLIARO_MAP_01'],
      team: ['CLIARO_TEAM_01'],
      gallery: ['CLIARO_GALLERY_01'],
      hours: ['CLIARO_HOURS_01'],
      menu: ['CLIARO_MENU_01']
    }
  },

  stylist: {
    id: 'stylist',
    name: 'Luxe',
    description: 'Luxury and elegant. Perfect for premium brands and upscale services.',
    preview_image: '/theme-previews/stylist.png',
    recommended_for: ['salon', 'legal', 'realestate', 'photography', 'events', 'church'],
    best_for_label: 'Luxury & premium brands',
    prefix: 'STYLIST_',
    defaultStyleGuide: {
      colors: {
        primary: "#C17842",
        secondary: "#2E3A52",
        background: "#F5EDE4",
        text: "#2E3A52",
        muted: "#8a8a8a"
      },
      typography: {
        heading: { font_family: "Playfair Display", font_weight: 700 },
        body: { font_family: "DM Sans", font_weight: 400 }
      },
      ui: {
        button: {
          border_radius: "4px",
          padding: "16px 40px",
          background: "primary",
          text_color: "#ffffff",
          font_weight: "500",
          box_shadow: "none"
        },
        input: {
          border_radius: "4px",
          border_color: "#d4c5b0",
          padding: "14px 16px",
          background: "#ffffff",
          focus_border: "#C17842",
          box_shadow: "none"
        }
      }
    },
    components: {
      common: ['STYLIST_HEADER_01', 'STYLIST_FOOTER_01'],
      hero: ['STYLIST_HERO_01'],
      about: ['STYLIST_ABOUT_01'],
      services: ['STYLIST_SERVICES_01'],
      features: ['STYLIST_FEATURES_01'],
      testimonials: ['STYLIST_TESTIMONIAL_01'],
      contact: ['STYLIST_CONTACT_01'],
      cta: ['STYLIST_CTA_01'],
      faq: ['STYLIST_FAQ_01'],
      pricing: ['STYLIST_PRICING_01'],
      stats: ['STYLIST_STATS_01'],
      content: ['STYLIST_CONTENT_01'],
      newsletter: ['STYLIST_NEWSLETTER_01'],
      map: ['STYLIST_MAP_01'],
      team: ['STYLIST_TEAM_01'],
      gallery: ['STYLIST_GALLERY_01'],
      hours: ['STYLIST_HOURS_01'],
      menu: ['STYLIST_MENU_01']
    }
  },

  zypgo: {
    id: 'zypgo',
    name: 'Volt',
    description: 'Dark and bold. Built for tech products, SaaS, and modern startups.',
    preview_image: '/theme-previews/zypgo.png',
    recommended_for: ['saas', 'agency', 'gym', 'automotive', 'photography'],
    best_for_label: 'Tech products & modern startups',
    prefix: 'ZYPGO_',
    defaultStyleGuide: {
      colors: {
        primary: "#4ADE50",
        secondary: "#0D0D0D",
        background: "#111111",
        text: "#f0f0f0",
        muted: "#888888"
      },
      typography: {
        heading: { font_family: "Inter", font_weight: 700 },
        body: { font_family: "Inter", font_weight: 400 }
      },
      ui: {
        button: {
          border_radius: "50px",
          padding: "14px 32px",
          background: "primary",
          text_color: "#0D0D0D",
          font_weight: "600",
          box_shadow: "0 0 20px rgba(74, 222, 80, 0.3)"
        },
        input: {
          border_radius: "12px",
          border_color: "#333333",
          padding: "14px 16px",
          background: "#1a1a1a",
          focus_border: "#4ADE50",
          box_shadow: "0 0 10px rgba(74, 222, 80, 0.1)"
        }
      }
    },
    components: {
      common: ['ZYPGO_HEADER_01', 'ZYPGO_FOOTER_01'],
      hero: ['ZYPGO_HERO_01'],
      about: ['ZYPGO_ABOUT_01'],
      services: ['ZYPGO_SERVICES_01'],
      features: ['ZYPGO_FEATURES_01'],
      testimonials: ['ZYPGO_TESTIMONIAL_01'],
      contact: ['ZYPGO_CONTACT_01'],
      cta: ['ZYPGO_CTA_01'],
      faq: ['ZYPGO_FAQ_01'],
      pricing: ['ZYPGO_PRICING_01'],
      stats: ['ZYPGO_STATS_01'],
      content: ['ZYPGO_CONTENT_01'],
      newsletter: ['ZYPGO_NEWSLETTER_01'],
      map: ['ZYPGO_MAP_01'],
      team: ['ZYPGO_TEAM_01'],
      gallery: ['ZYPGO_GALLERY_01'],
      hours: ['ZYPGO_HOURS_01'],
      menu: ['ZYPGO_MENU_01']
    }
  }
};

// Helper: get all component codes for a theme (flat array)
export function getThemeComponentCodes(themeId) {
  const theme = THEMES[themeId] || THEMES.classic;
  return Object.values(theme.components).flat();
}

// Helper: get the default style guide for a theme
export function getThemeStyleGuide(themeId) {
  const theme = THEMES[themeId] || THEMES.classic;
  return theme.defaultStyleGuide;
}

// Helper: get components for a specific category within a theme
export function getThemeCategoryComponents(themeId, category) {
  const theme = THEMES[themeId] || THEMES.classic;
  return theme.components[category] || [];
}

// Helper: get the header component code for a theme
export function getThemeHeaderCode(themeId) {
  const theme = THEMES[themeId] || THEMES.classic;
  const common = theme.components.common || [];
  return common.find(c => c.includes('HEADER')) || 'HEADER_01';
}

// Helper: get the footer component code for a theme
export function getThemeFooterCode(themeId) {
  const theme = THEMES[themeId] || THEMES.classic;
  const common = theme.components.common || [];
  return common.find(c => c.includes('FOOTER')) || 'FOOTER_01';
}

export default THEMES;
